<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="single-blog">
    <div class="wrapper">
      <div class="single-infos">
        <ArticleHeader
          ref="headerRef"
          :content="[content.author, ...content.details]"
          :class="{
            'article-header__button':
              languageSupported.length > 0 && options.length > 1,
          }"
          class="mb-d single-blog__header"
        />
      </div>

      <GPicture
        class="m-d single-blog__picture"
        :content="content"
        fetchpriority="high"
      />
      <h1 class="h-visually-hidden" v-html="content.title"></h1>
      <div
        ref="titleRef"
        :class="!content.subtitle ? 'border-bottom pb-d mb-d' : ''"
        class="h1 split-title single-blog__title"
        v-html="content.title"
      ></div>
      <span
        v-if="content.subtitle"
        class="h2 pb-d mb-d single-blog__subtitle border-bottom"
        v-html="content.subtitle"
      ></span>
    </div>

    <div
      v-for="(cpt, i) in content.flexibleContent"
      :key="i"
      :class="cptClass(cpt.component as string) || defaultClasses"
    >
      <component
        :is="component[cpt.component as keyof typeof flexibleComponentList]"
        v-if="
          component[cpt.component as keyof typeof flexibleComponentList] &&
          cpt.content
        "
        :content="cpt.content"
        :is-polaroid="cpt.component === 'picture'"
        :is-test="cpt.component === 'picture'"
      />
    </div>

    <div class="wrapper">
      <template v-if="chrome.newsletter">
        <hr />

        <div class="newsletter-cta m-d">
          <img
            v-if="chrome.newsletter.picture"
            v-src="chrome.newsletter.picture"
            :alt="chrome.newsletter.picture.alt"
            :width="chrome.newsletter.picture.width"
            :height="chrome.newsletter.picture.height"
            loading="lazy"
            class="newsletter-cta__picture"
          />
          <div class="newsletter-cta__content">
            <h2 class="newsletter-cta__title h2">
              {{ chrome.newsletter.title }}
            </h2>

            <hr />

            <div
              v-html="chrome.newsletter.htmltext"
              class="newsletter-cta__wysiwyg"
            ></div>

            <hr />

            <button data-newsletter-trigger class="newsletter-cta__button">
              {{ chrome.newsletter.btnLabel }}
              <SvgSprite
                class="newsletter-cta__button__icon"
                symbol="ui-full-arrow"
                size="28"
              />
            </button>
          </div>
        </div>
      </template>

      <div v-if="content.related.length" class="blog__similar mt-d">
        <hr />
        <h2 class="blog__similar__title m-d section-label">
          {{ chrome.language === 'en' ? "What's next" : 'Découvrir plus' }}
        </h2>
        <div class="blog__similar__items pb-d">
          <ArticleCard
            v-for="(article, index) in content.related"
            :key="index"
            :content="article"
            title-tag="h3"
            class="blog__similar__items__card"
          />
        </div>
      </div>
      <hr />

      <Cta :content="content.nextArticle || content.cta" class="m-d">
        <template v-if="content.nextArticle" #headline>
          <h2 class="section-label">
            {{ content.nextArticle?.headline || content.cta?.headline }}
          </h2>
        </template>
      </Cta>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'

import ArticleCard from '@/components/ArticleCard.vue'
import ArticleHeader from '@/components/ui/ArticleHeader.vue'
import Cta from '@/components/ui/Cta.vue'

import { createSplitTitle, SplitTitle } from '@/animations/splitTitle'
import isMobile from 'ismobilejs'

import { useResourceStore } from '@/stores/resource'
import { useChromeStore } from '@/stores/chrome'
import { getlanguageSupported } from '@/config/languages'

import type { Language } from '@/config/languages'
import type { ComponentType } from '@/types'
import flexibleComponentList from '@/modules/flexibleComponentList'

defineProps({
  content: {
    type: Object,
    required: true,
  },
})

const languageSupported = getlanguageSupported()
const titleRef = ref<HTMLElement>()
const headerRef = ref<ComponentType<typeof ArticleHeader>>()
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const component = flexibleComponentList()
const chrome = useChromeStore()
const resource = useResourceStore()

const options = computed(() => {
  if (resource.languages) {
    return Object.keys(resource.languages).map(lang => {
      const value = resource.languages[lang as Language]

      return {
        label: lang,
        value,
      }
    })
  }

  return []
})

const cptClass = (name: string) => {
  const classes: Record<string, string[]> = {
    text: ['wrapper', 'm-intro-text'],
    cta: ['wrapper', 'single-blog__polaroid'],
    picture: ['wrapper', 'single-blog__polaroid'],
    gallery: ['m-d'],
    keyfacts: ['wrapper', 'm-d', 'single-blog__keyfacts'],
  }

  return classes[name]
}
const defaultClasses = ['m-d', 'wrapper']

let splitTitle: SplitTitle
onMounted(() => {
  if (!titleRef.value || !headerRef.value) {
    return
  }
  if (!isMobile().phone) {
    splitTitle = createSplitTitle(titleRef.value)
    splitTitle.intro.play()
  }
  headerRef.value.play()
})
</script>

<style lang="scss" scoped>
$bp: m;

:deep() {
  .article-header__button {
    @include mq($until: s) {
      padding-bottom: 1.8rem;
    }
  }
}

.single-blog__header {
  @include mq(s) {
    margin-top: 2rem;
  }
}

.single-blog__picture {
  :deep(figcaption) {
    display: none;
  }
}

.single-blog__polaroid {
  text-align: center;

  + .single-blog__polaroid .polaroid {
    // border-top: 0;
    @include fluid(
      margin-top,
      (
        xxs: 40px,
        xxl: 80px,
      )
    );
  }

  :deep(.picture:not(.no-stretch) img) {
    width: 100%;
  }
}

.border-bottom {
  border-bottom: 1px solid rgba(var(--c-foreground-rgb), 0.5);
}

.single-blog__title {
  // font-size: 100px;
  // line-height: 110px;
  @include fluid(
    font-size,
    (
      xxs: 50px,
      xxl: 100px,
    )
  );
  @include fluid(
    line-height,
    (
      xxs: 40px,
      xxl: 110px,
    )
  );
}

.single-blog__subtitle {
  @include fluid(
    font-size,
    (
      xxs: 22px,
      xxl: 50px,
    )
  );
  @include fluid(
    line-height,
    (
      xxs: 32px,
      xxl: 60px,
    )
  );

  display: block;
  margin-top: 1rem;
  font-family: $ff-default;
  font-size: 36px;
  font-weight: 500;
  line-height: 46px;
}

.blog__similar__title {
  display: block;
  text-align: center;
}

.blog__similar__items {
  @include scrollbars($foreground-color: var(--c-foreground));

  display: flex;
  justify-content: space-between;
  overflow: auto;
}

.blog__similar__items__card {
  // prettier-ignore
  @include fluid(width,(xxs: 290px, wrapper: 375px));

  flex-shrink: 0;
  margin: 0 1rem;

  :deep(.article-card) {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  :deep(.article-card__title) {
    font-family: $ff-alt;
  }

  :deep(.article-card__footer) {
    margin-top: auto;
  }

  @include mq(m) {
    margin: 0 2rem;
  }

  @include mq(wrapper) {
    width: calc(33.33% - 4rem);
  }
}

.single-blog__keyfacts {
  + .single-blog__keyfacts {
    @include fluid(
      margin-top,
      (
        xxs: -41px,
        xxl: -81px,
      )
    );

    margin-top: -81px;
  }
}

.single-infos {
  position: relative;
}

.newsletter-cta {
  // prettier-ignore
  @include fluid(padding, (xs: 20px, m: 30px) );

  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4rem;
  color: var(--c-background);
  background-color: var(--c-foreground);
  border-radius: 0.4rem;

  hr {
    width: 100%;
    margin: 0;
    background-color: currentcolor;
  }

  @include mq($bp) {
    flex-direction: row;
  }
}

.newsletter-cta__picture {
  width: 100%;
  object-fit: cover;

  @include mq($bp) {
    width: 53%;
  }
}

.newsletter-cta__title {
  text-transform: uppercase;
}

.newsletter-cta__content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2rem;
}

.newsletter-cta__wysiwyg {
  :deep() {
    > *:last-child {
      margin-bottom: 0;
    }

    ul {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      padding: 0;
    }

    li {
      list-style-type: none;

      &::before {
        content: '';
        display: inline-block;
        width: 1.4rem;
        height: 1.4rem;
        background-color: currentcolor;
        mask-image: url('data:image/svg+xml,<svg width="29" height="29" viewBox="0 0 29 29" xmlns="http://www.w3.org/2000/svg"><path d="M25.9578 13.5019C25.9787 13.5584 25.9862 13.617 25.9985 13.6767C26.0069 13.7176 26.0287 13.7523 26.0328 13.7946C26.0346 13.8129 26.0264 13.829 26.0274 13.8471C26.0285 13.8685 26.0397 13.8867 26.0397 13.9085C26.0397 13.9274 26.0297 13.9433 26.0289 13.962C26.0281 13.9819 26.0369 13.9995 26.035 14.0195C26.0305 14.0675 26.0055 14.1063 25.9956 14.1522C25.9843 14.2054 25.9775 14.2574 25.9592 14.3079C25.9463 14.3431 25.9348 14.3771 25.9188 14.4107C25.8896 14.4734 25.8499 14.5256 25.8107 14.5815C25.7838 14.6199 25.7601 14.6576 25.7288 14.6925C25.7056 14.7185 25.6852 14.7443 25.6598 14.7679C25.6079 14.8168 25.5489 14.8524 25.4888 14.891C25.4489 14.9169 25.4121 14.943 25.369 14.9641C25.3377 14.9793 25.3087 14.9948 25.2759 15.0074C25.2134 15.0309 25.1477 15.0399 25.0806 15.0526C25.0422 15.0603 25.0104 15.0824 24.9706 15.0863C24.6609 15.1176 17.3753 15.9209 15.9559 23.018C15.8288 23.659 15.2075 24.0728 14.563 23.9462C13.922 23.8179 13.5065 23.1943 13.6348 22.5534C14.3754 18.8518 16.4274 16.5419 18.5684 15.0921L3.55088 15.0921C2.89728 15.0921 2.36725 14.5621 2.36725 13.9085C2.36725 13.2549 2.89728 12.7249 3.55088 12.7249L18.5684 12.7249C16.4274 11.275 14.3754 8.96508 13.6348 5.26365C13.5065 4.62212 13.922 3.99906 14.563 3.87075C14.6416 3.85454 14.7196 3.84767 14.7965 3.84767C15.349 3.84767 15.8438 4.23602 15.9559 4.79895C17.3753 11.8961 24.6609 12.6994 24.9672 12.7301C25.017 12.7348 25.0574 12.7611 25.1051 12.7717C25.1708 12.7863 25.2392 12.7896 25.3006 12.8146C25.3239 12.8241 25.3426 12.8394 25.3651 12.8503C25.4224 12.8779 25.4695 12.9153 25.521 12.9513C25.5702 12.9855 25.623 13.0123 25.6665 13.0536C25.6881 13.074 25.7052 13.0968 25.7252 13.1189C25.7657 13.1635 25.7944 13.2136 25.8278 13.2638C25.8599 13.3121 25.8956 13.3555 25.9205 13.4087C25.935 13.4394 25.9459 13.47 25.9579 13.5022L25.9578 13.5019Z" /></svg>');
        mask-size: contain;
        mask-repeat: no-repeat;
        margin-inline-end: 1rem;
      }
    }
  }
}

.newsletter-cta__button {
  display: flex;
  gap: 1rem;
  align-items: center;
  padding: 0;
  color: currentcolor;
  font-family: $ff-default;
  font-size: 1.4rem;
  font-style: normal;
  font-weight: 700;
  line-height: 2.5rem;
  text-transform: uppercase;
  letter-spacing: 0.1rem;
  background-color: transparent;
  border-width: 0;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
  }
}

.newsletter-cta__button__icon {
  display: block;
  width: 2.9rem;
  height: 2.9rem;
  fill: currentcolor;
}
</style>
